import { ICallResult } from '../stores/core/models';
import { normalizeErrorKey, isBlockedOperationError } from '../core/utils/error-utils';

export interface IPartialDeleteResult {
  isPartialDelete: boolean;
  deletedCount: number;
  blockedCount: number;
}

export class PartialDeleteService {
  /**
   * Detects and handles partial delete scenarios for any entity type
   * @param callResult The API response result
   * @param entityType The type of entity being deleted (e.g., 'indexes')
   * @returns PartialDeleteResult with information about the operation
   */
  static detectPartialDelete(callResult: ICallResult, entityType: string): IPartialDeleteResult {
    if (!callResult.errors?.length) {
      return { isPartialDelete: false, deletedCount: 0, blockedCount: 0 };
    }

    const firstError: any = callResult.errors[0];
    const rawKey: string = firstError?.errorKey ?? firstError?.code ?? '';
    
    // Use utility function to normalize error key
    const normalizedKey = normalizeErrorKey(rawKey);

    // Extract counts from response data
    const responseData: any = (callResult as any)?.data ?? {};
    const deletedCount = Number(responseData?.deletedCount ?? 0);
    const blockedCount = Number(responseData?.blockedCount ?? 0);

    // Check if this is a partial delete scenario using utility function
    const isPartialDelete = isBlockedOperationError(normalizedKey, entityType) &&
                           deletedCount > 0 &&
                           blockedCount > 0;

    return {
      isPartialDelete,
      deletedCount,
      blockedCount
    };
  }

  /**
   * Gets the appropriate notification keys for partial delete scenarios
   * @param entityType The type of entity being deleted
   * @returns Object with title and message keys
   */
  static getPartialDeleteNotificationKeys(entityType: string) {
    return {
      title: `${entityType}.notifications.partialDeleteTitle`,
      message: `${entityType}.notifications.partialDeleteMsg`
    };
  }
} 